<template>
  <div class="w-full fadeIn flex gap-4 flex-wrap bg-gray-100 p-4 rounded-xl">
    <!-- About section -->
    <article class="flex-[3] bg-white shadow-sm rounded-xl p-8 flex min-w-full gap-5">
      <img
        v-if="applicantProfile?.user_details?.avatar_image"
        :src="applicantProfile?.user_details?.avatar_image"
        alt="Avatar"
        class="h-40 aspect-square object-cover rounded-2xl"
      />
      <div
        v-else
        class="w-32 h-32 bg-gray-200 aspect-square rounded-full flex items-center justify-center"
      >
        <User class="w-12 h-12 text-gray-500" />
      </div>
      <div class="w-full max-w-3xl">
        <!-- Text -->
        <h3 class="text-gray-600">
          About {{ applicantProfile?.user_details?.first_name }} (core referral text)
        </h3>
        <p class="mt-4">{{ applicantProfile?.referral_package?.core_referral_text }}</p>

        <!-- Links -->
        <div class="flex items-center gap-2 mt-2">
          <a
            :href="applicantProfile?.referral_package?.resume_link"
            target="_blank"
            class="border-[1px] border-gray-300 rounded-md px-2 py-1 text-sm text-gray-500 hover:bg-white shadow-sm flex items-center gap-2"
          >
            <File class="w-4 h-4" />
            <span class="text-sm">Resume</span>
          </a>
          <a
            :href="`https://outtalent.com/profile/${applicantProfile?.user_details?.username}`"
            target="_blank"
            class="border-[1px] border-gray-300 rounded-md px-2 py-1 text-sm text-gray-500 hover:bg-white shadow-sm flex items-center gap-2"
          >
            <User class="w-4 h-4" />
            Profile
          </a>
          <a
            v-if="applicantProfile?.user_profile?.linkedin_url"
            :href="applicantProfile?.user_profile?.linkedin_url"
            target="_blank"
          >
            <img src="/linkedin_logo.svg" alt="LinkedIn" class="w-5 h-5" />
          </a>
          <a
            v-if="applicantProfile?.user_profile?.telegram_handle"
            :href="`https://t.me/${applicantProfile?.user_profile?.telegram_handle}`"
            target="_blank"
          >
            <img src="/telegram_logo.svg" alt="Telegram" class="w-5 h-5" />
          </a>
        </div>
      </div>
    </article>
    
    <!-- Launchpad data section -->
    <article class="w-full sm:w-[calc(50%-0.5rem)] md:w-full lg:w-[calc(50%-0.5rem)] bg-white shadow-sm rounded-xl p-4 flex flex-col">
      <span class="text-xl rounded-full bg-cosmicPurple/20 px-4 py-1 text-cosmicPurple w-fit">{{
        applicantProfile?.fellow_data?.batch
      }}</span>
      <span class="text-5xl mt-4">{{ applicantProfile?.launchpad.on_track_percentage }}%</span>
      <span>program completion</span>

      <hr class="my-4" />
      <div class="flex items-center gap-2 flex-wrap">
        <span
          v-if="applicantProfile?.launchpad?.backlog_tasks_count > 0"
          class="flex-1 rounded-full border-[1px] border-gray-400 px-4 py-1 text-gray-700 text-sm min-w-32"
          >{{ applicantProfile?.launchpad?.backlog_tasks_count }} backlog tasks</span
        >
        <span
          class="flex-1 bg-lime-100 text-lime-600 px-4 py-1 rounded-full capitalize text-sm min-w-32"
          >{{ applicantProfile?.launchpad?.status }} (Week
          {{ applicantProfile?.expedition?.current_week }})</span
        >
      </div>
    </article>

    <!-- Contract data section -->
    <article v-if="isGuide" class="w-full sm:w-[calc(50%-0.5rem)] md:w-full lg:w-[calc(50%-0.5rem)] bg-white shadow-sm rounded-xl p-4 flex flex-col">
      <span class="text-gray-600">Contract</span>
      <span class="text-5xl mt-4">{{ applicantProfile?.contract?.contract_take_rate * 100 }}%</span>
      <span class="text-xl rounded-full">{{ applicantProfile?.fellow_data?.contract_type }}</span>
      <span class="text-gray-600 mt-4"
        >Signed: {{ formatDate(applicantProfile?.contract?.contract_isa_signing_date) }}</span
      >
    </article>

    <!-- Outtalent statuses section -->
    <article
      v-if="isGuide"
      class="w-full sm:w-[calc(50%-0.5rem)] md:w-full lg:w-[calc(50%-0.5rem)] bg-white shadow-sm rounded-xl p-4 flex flex-col justify-between"
    >
      <div class="flex items-center justify-between mb-4">
        <span class="text-gray-600">OutTalent Status</span>
      </div>
      <div>
        <div class="flex items-center justify-between">
          <span class="flex items-center gap-2">
            <CodeXml class="h-5 w-5" />
            Program Status:</span
          >
          <span
            class="inline-flex items-center px-3 py-1.5 rounded-full text-sm bg-purple-100 text-purple-700"
          >
            {{
              getProgramStatusLabel(applicantProfile?.fellow_data?.outtalent_program_status) ||
              'Not set'
            }}
          </span>
        </div>

        <hr class="my-2 border-gray-500" />
        <div class="flex items-center justify-between">
          <span class="flex gap-2 items-center">
            <GraduationCap class="h-5 w-5" />
            Graduation Status:</span
          >
          <span
            class="inline-flex items-center px-3 py-1.5 rounded-full text-sm bg-blue-100 text-blue-700"
          >
            {{
              getGraduationStatusLabel(
                applicantProfile?.fellow_data?.outtalent_graduation_status
              ) || 'Not set'
            }}
          </span>
        </div>

        <hr class="my-2 border-gray-500" />

        <div class="flex items-center justify-between">
          <span class="flex items-center gap-2">
            <Handshake class="h-5 w-5" />
            Relationship Status:</span
          >
          <span
            class="inline-flex items-center px-3 py-1.5 rounded-full text-sm bg-emerald-100 text-emerald-700"
          >
            {{
              getRelationshipLabel(applicantProfile?.fellow_data?.outtalent_relationship) ||
              'Not set'
            }}
          </span>
        </div>
      </div>
    </article>

      <!-- Program statuses section -->
      <article
      class="w-full sm:w-[calc(50%-0.5rem)] md:w-full lg:w-[calc(50%-0.5rem)] bg-white shadow-sm rounded-xl p-4 flex flex-col justify-between"
    >
      <div class="flex items-center justify-between mb-4">
        <span class="text-gray-600">Program Stage Status</span>
        <p
          v-if="applicantProfile?.referral_package?.referral_eligible"
          class="text-sm bg-lime-100 text-lime-600 px-4 py-1 rounded-full"
        >
          Referrable
        </p>
        <p v-else class="text-sm bg-red-100 text-red-600 px-4 py-1 rounded-full">
          Not Referrable yet
        </p>
      </div>
      <div>
        <div class="flex items-center justify-between">
          <span class="flex items-center gap-2">
            <File class="h-5 w-5" />
            Resume status:</span
          >
          <span
            class="inline-flex items-center px-3 py-1.5 rounded-full text-sm bg-purple-100 text-purple-700"
          >
            {{
              applicantProfile?.fellow_data?.resume_status ||
              'Not set'
            }}
          </span>
        </div>

        <hr class="my-2 border-gray-500" />
        <div class="flex items-center justify-between">
          <span class="flex gap-2 items-center">
            <Speech class="h-5 w-5" />
            Behavioral stage status:</span
          >
          <span
            class="inline-flex items-center px-3 py-1.5 rounded-full text-sm bg-blue-100 text-blue-700"
          >
            {{
             
                applicantProfile?.fellow_data?.behavioral_status
               || 'Not set'
            }}
          </span>
        </div>

        <hr class="my-2 border-gray-500" />

        <div class="flex items-center justify-between">
          <span class="flex items-center gap-2">
            <CodeXml class="h-5 w-5" />
            Coding Status:</span
          >
          <span
            class="inline-flex items-center px-3 py-1.5 rounded-full text-sm bg-emerald-100 text-emerald-700"
          >
            {{
              applicantProfile?.fellow_data?.coding_status ||
              'Not set'
            }}
          </span>
        </div>

        <hr class="my-2 border-gray-500" />

        <div class="flex items-center justify-between">
          <span class="flex items-center gap-2">
            <MonitorCog class="h-5 w-5" />
            System Design Status:</span
          >
          <span
            class="inline-flex items-center px-3 py-1.5 rounded-full text-sm bg-emerald-100 text-emerald-700"
          >
            {{
              applicantProfile?.fellow_data?.system_design_status ||
              'Not set'
            }}
          </span>
        </div>
      </div>
    </article>

    <!-- Internal comments section -->
    <article
      v-if="
        isGuide &&
        (applicantProfile?.internal_comments?.length &&
        applicantProfile?.internal_comments?.length > 0)
      "
      class="flex-[3] bg-white shadow-sm rounded-xl p-8 min-w-full gap-4"
    >
      <h3 class="text-gray-600">Internal Comments</h3>
      <div class="flex flex-col gap-2 mt-4">
        <div
          v-for="comment in applicantProfile?.internal_comments"
          :key="comment.id"
          class="ring-[1px] ring-gray-300/50 p-4 shadow-sm rounded-md bg-white hover:shadow-md transition-all duration-300 relative gap-2"
        >
          <div class="flex items-center text-sm">
            <img
              v-if="comment.created_by.avatar_image"
              :src="comment.created_by.avatar_image"
              :alt="comment.created_by.first_name"
              class="h-8 w-8 object-cover rounded-full"
            />
            <span v-else class="h-8 w-8 bg-gray-200 flex justify-center items-center rounded-full">
              <UserRound class="w-4 h-4 text-black" />
            </span>
            <div class="ml-2">
              <p v-if="comment.created_by.username === 'admin'" class="font-semibold">
                Legacy Airtable comment
              </p>
              <p v-else class="font-semibold">
                {{ comment.created_by.first_name }} {{ comment.created_by.last_name }}
              </p>
              <p class="text-gray-500">{{ formatDate(comment.created_at) }}</p>
            </div>
          </div>
          <p class="mt-2">{{ comment.comment }}</p>
        </div>
      </div>
    </article>
  </div>
</template>

<script setup lang="ts">
import {
  getGraduationStatusLabel,
  getRelationshipLabel,
  getProgramStatusLabel
} from '@/const/fellowStatuses'
import type { ApplicantProfile } from '@/types/applicantProfile'
import { formatDate } from '@/utils/formatDate'
import { CodeXml, GraduationCap, Handshake, File, User, Speech, MonitorCog } from 'lucide-vue-next'
defineProps<{
  applicantProfile: ApplicantProfile | null
}>()
import { useAuth } from '@/composables/useAuth'

const { isGuide } = useAuth()
</script>
